import { index, pgEnum, pgTable, text, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import z from "zod";

import { usStatesEnumSchema, type UsStatesEnumType } from "./commonSchema";
import { customTimestamp } from "./utils/types";

// Individual brokers are named people ("this agent will be Jane Doe"); organizations
// are agencies ("this agent will be from Acme Insurance"). Drives the consent copy.
export const brokerTypeEnumSchema = z.enum(["INDIVIDUAL", "ORGANIZATION"]);
export type BrokerTypeEnumType = z.infer<typeof brokerTypeEnumSchema>;
export const brokerTypeEnum = pgEnum(
  "broker_type_enum",
  brokerTypeEnumSchema.enum,
);

export const brokersTable = pgTable(
  "brokers",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    licenseNumber: text("license_number"),
    type: brokerTypeEnum("type").notNull(),
    // Stored as text[] rather than us_states_enum[] so GCP Datastream can replicate
    // it to BigQuery (Datastream cannot map arrays of enum types). Values are still
    // validated against usStatesEnumSchema at the API layer below. See issue #512.
    usStates: text("us_states")
      .array()
      .$type<UsStatesEnumType[]>()
      .notNull()
      .default([]),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    // GIN index supports the `@>` array-containment lookup used to resolve a
    // broker by US state (see getBrokerByUsStateOperation).
    index("brokers_us_states_index").using("gin", table.usStates),
  ],
);

export const brokerSchema = createSelectSchema(brokersTable, {
  usStates: z.array(usStatesEnumSchema),
  type: brokerTypeEnumSchema,
});
export type BrokerType = z.infer<typeof brokerSchema>;

export const insertBrokerSchema = createInsertSchema(brokersTable, {
  usStates: z.array(usStatesEnumSchema),
  type: brokerTypeEnumSchema,
}).omit({
  createdAt: true,
  updatedAt: true,
});

export type InsertBrokerType = z.infer<typeof insertBrokerSchema>;
